<template>
  <v-app>
    <!-- App Bar -->
    <v-app-bar color="primary" elevation="2">
      <v-app-bar-title>
        <v-icon class="mr-2">mdi-broom</v-icon>
        Time Custodian
      </v-app-bar-title>
      <v-spacer></v-spacer>
      <v-btn v-if="isAdmin" icon @click="$router.push('/admin')" color="white">
        <v-icon color="yellow-darken-2">mdi-shield-account</v-icon>
      </v-btn>
      <v-btn icon @click="logout" color="white">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-4">
        <!-- Welcome Header -->
        <v-card class="mb-4 pa-4" elevation="1">
          <h2 class="text-h5 mb-2">Welcome, {{ username }}!</h2>
          <p class="text-subtitle-2 text-grey">{{ currentDate }}</p>
          <p v-if="closetLocation" class="text-subtitle-2 text-primary mt-1">
            <v-icon size="small" class="mr-1">mdi-locker</v-icon>
            Closet: {{ closetLocation }}
          </p>
        </v-card>

        <!-- Admin Access Card (only for admins) -->
        <v-card 
          v-if="isAdmin"
          class="mb-4 pa-4" 
          elevation="2" 
          color="purple-lighten-5"
          @click="$router.push('/admin')"
          style="cursor: pointer;"
        >
          <div class="d-flex align-center">
            <v-icon size="40" color="purple" class="mr-3">mdi-shield-account</v-icon>
            <div class="flex-grow-1">
              <h3 class="text-h6 text-purple">Admin Dashboard</h3>
              <p class="text-caption text-grey mb-0">Manage users, tasks, and view statistics</p>
            </div>
            <v-icon color="purple">mdi-chevron-right</v-icon>
          </div>
        </v-card>

        <!-- Task Summary Cards -->
        <v-row class="mb-4">
          <v-col cols="6">
            <v-card class="text-center pa-4" color="blue-lighten-5" elevation="2">
              <v-icon size="32" color="primary" class="mb-2">mdi-clipboard-text</v-icon>
              <div class="text-h4 font-weight-bold">{{ activeTasks.length }}</div>
              <div class="text-subtitle-2">Active Tasks</div>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card class="text-center pa-4" color="green-lighten-5" elevation="2">
              <v-icon size="32" color="success" class="mb-2">mdi-check-circle</v-icon>
              <div class="text-h4 font-weight-bold">{{ completedTasks.length }}</div>
              <div class="text-subtitle-2">Completed</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Navigation Cards -->
        <v-row class="mb-4">
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'active' ? 'primary' : 'white'"
              :class="tab === 'active' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'active'"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'active' ? 'white' : 'primary'" class="mb-2">mdi-clipboard-list</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Active Tasks</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'completed' ? 'primary' : 'white'"
              :class="tab === 'completed' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'completed'"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'completed' ? 'white' : 'success'" class="mb-2">mdi-check-all</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Completed</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'create' ? 'primary' : 'white'"
              :class="tab === 'create' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'create'"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'create' ? 'white' : 'primary'" class="mb-2">mdi-plus-circle</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Create Task</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'supplies' ? 'primary' : 'white'"
              :class="tab === 'supplies' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'supplies'"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'supplies' ? 'white' : 'primary'" class="mb-2">mdi-package-variant</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Supplies</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'inspections' ? 'primary' : 'white'"
              :class="tab === 'inspections' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'inspections'; loadMyInspections()"
              style="cursor: pointer;"
            >
              <v-badge v-if="myInspectionsCount > 0" :content="myInspectionsCount" color="deep-purple" floating>
                <v-icon size="32" :color="tab === 'inspections' ? 'white' : 'deep-purple'" class="mb-2">mdi-clipboard-check</v-icon>
              </v-badge>
              <v-icon v-else size="32" :color="tab === 'inspections' ? 'white' : 'deep-purple'" class="mb-2">mdi-clipboard-check</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Inspections</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'my-inventory' ? 'primary' : 'white'"
              :class="tab === 'my-inventory' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'my-inventory'; loadMyInventory()"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'my-inventory' ? 'white' : 'teal'" class="mb-2">mdi-archive-outline</v-icon>
              <div class="text-subtitle-2 font-weight-bold">My Inventory</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              :color="tab === 'incident' ? 'primary' : 'white'"
              :class="tab === 'incident' ? 'text-white' : ''"
              elevation="2"
              class="pa-4 text-center"
              @click="tab = 'incident'"
              style="cursor: pointer;"
            >
              <v-icon size="32" :color="tab === 'incident' ? 'white' : 'orange-darken-2'" class="mb-2">mdi-alert-circle-outline</v-icon>
              <div class="text-subtitle-2 font-weight-bold">Report Incident</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Content -->
        <v-window v-model="tab" :touch="false">
          <!-- Active Tasks Tab -->
          <v-window-item value="active">
            <!-- Search Filter -->
            <v-text-field
              v-model="searchQuery"
              label="Search tasks"
              placeholder="Find by name, location, or details..."
              prepend-inner-icon="mdi-magnify"
              clearable
              density="compact"
              variant="outlined"
              class="mb-4"
            ></v-text-field>

            <v-card v-if="filteredActiveTasks.length === 0 && activeTasks.length > 0" class="pa-4 text-center" elevation="1">
              <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-magnify-close</v-icon>
              <p class="text-h6 text-grey">No tasks match your search</p>
            </v-card>

            <v-card v-else-if="activeTasks.length === 0" class="pa-8 text-center" elevation="1">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-check-circle-outline</v-icon>
              <p class="text-h6 text-grey">No active tasks</p>
              <p class="text-body-2 text-grey">Great job! All tasks are completed.</p>
            </v-card>
            
            <v-list v-else class="py-0">
              <v-card
                v-for="task in filteredActiveTasks"
                :key="task.id"
                class="mb-3"
                elevation="2"
                :class="{ 'task-in-progress': task.status === 'in_progress' }"
              >
                <v-card-text class="pb-2">
                  <div class="d-flex justify-space-between align-start mb-2">
                    <h3 class="text-h6">{{ task.title }}</h3>
                    <v-chip
                      :color="getPriorityColor(task.priority)"
                      size="small"
                      class="ml-2"
                    >
                      {{ task.priority }}
                    </v-chip>
                  </div>
                  
                  <!-- Status Badge -->
                  <v-chip
                    v-if="task.status === 'in_progress' || task.status === 'paused'"
                    :color="task.status === 'in_progress' ? 'blue' : 'orange'"
                    size="small"
                    class="mb-2"
                  >
                    <v-icon start size="small">
                      {{ task.status === 'in_progress' ? 'mdi-play' : 'mdi-pause' }}
                    </v-icon>
                    {{ task.status === 'in_progress' ? 'In Progress' : 'Paused' }}
                  </v-chip>
                  
                  <p class="text-body-2 mb-2">{{ task.description }}</p>
                  
                  <div class="d-flex align-center mb-2">
                    <v-icon size="small" class="mr-1">mdi-map-marker</v-icon>
                    <span class="text-caption">{{ task.location }}</span>
                  </div>

                  <!-- Time Tracking Display -->
                  <div v-if="task.status !== 'pending'" class="d-flex align-center mb-2">
                    <v-icon size="small" class="mr-1">mdi-timer</v-icon>
                    <span class="text-caption font-weight-bold">
                      Time: {{ formatElapsedTime(task) }}
                    </span>
                  </div>

                  <!-- Task Notes -->
                  <v-textarea
                    v-model="taskNotes[task.id]"
                    label="Notes (Optional)"
                    placeholder="Explain any circumstances that caused delays or prevented completion (optional)..."
                    rows="2"
                    density="compact"
                    variant="outlined"
                    hint="Use this to document any issues or reasons the task took longer than expected"
                    class="mt-2"
                    @input="debounceSaveNotes(task.id)"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon size="small">mdi-note-text</v-icon>
                    </template>
                  </v-textarea>
                </v-card-text>
                
                <v-card-actions class="pt-0">
                  <!-- Pending Task Actions -->
                  <template v-if="task.status === 'pending'">
                    <v-btn
                      color="primary"
                      variant="elevated"
                      @click="startTask(task.id)"
                      prepend-icon="mdi-play"
                      block
                    >
                      Start Task
                    </v-btn>
                  </template>

                  <!-- In Progress Task Actions -->
                  <template v-else-if="task.status === 'in_progress'">
                    <v-btn
                      color="warning"
                      variant="elevated"
                      @click="pauseTask(task.id)"
                      prepend-icon="mdi-pause"
                      class="flex-grow-1"
                    >
                      Pause
                    </v-btn>
                    <v-btn
                      color="success"
                      variant="elevated"
                      @click="completeTask(task.id)"
                      prepend-icon="mdi-check"
                      class="flex-grow-1 ml-2"
                    >
                      Finish
                    </v-btn>
                  </template>

                  <!-- Paused Task Actions -->
                  <template v-else-if="task.status === 'paused'">
                    <v-btn
                      color="primary"
                      variant="elevated"
                      @click="resumeTask(task.id)"
                      prepend-icon="mdi-play"
                      class="flex-grow-1"
                    >
                      Resume
                    </v-btn>
                    <v-btn
                      color="success"
                      variant="elevated"
                      @click="completeTask(task.id)"
                      prepend-icon="mdi-check"
                      class="flex-grow-1 ml-2"
                    >
                      Finish
                    </v-btn>
                  </template>
                </v-card-actions>
                
                <!-- Mark Incomplete Button (always available for active tasks) -->
                <v-card-actions class="pt-0">
                  <v-btn
                    color="error"
                    variant="text"
                    size="small"
                    @click="openIncompleteDialog(task)"
                    prepend-icon="mdi-alert-circle"
                    block
                  >
                    Mark as Incomplete
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-list>
          </v-window-item>

          <!-- Completed Tasks Tab -->
          <v-window-item value="completed">
            <!-- Search Filter -->
            <v-text-field
              v-model="searchQuery"
              label="Search tasks"
              placeholder="Find by name, location, or details..."
              prepend-inner-icon="mdi-magnify"
              clearable
              density="compact"
              variant="outlined"
              class="mb-4"
            ></v-text-field>

            <v-card v-if="filteredCompletedTasks.length === 0 && completedTasks.length > 0" class="pa-4 text-center" elevation="1">
              <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-magnify-close</v-icon>
              <p class="text-h6 text-grey">No tasks match your search</p>
            </v-card>

            <v-card v-else-if="completedTasks.length === 0" class="pa-8 text-center" elevation="1">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-clipboard-check-outline</v-icon>
              <p class="text-h6 text-grey">No completed tasks yet</p>
              <p class="text-body-2 text-grey">Completed tasks will appear here.</p>
            </v-card>
            
            <v-list v-else class="py-0">
              <v-card
                v-for="task in filteredCompletedTasks"
                :key="task.id"
                class="mb-3"
                elevation="1"
              >
                <v-card-text>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <h3 class="text-h6 text-grey-darken-1">{{ task.title }}</h3>
                    <v-icon color="success">mdi-check-circle</v-icon>
                  </div>
                  
                  <p class="text-body-2 text-grey mb-2">{{ task.description }}</p>
                  
                  <div class="d-flex align-center mb-1">
                    <v-icon size="small" class="mr-1" color="grey">mdi-map-marker</v-icon>
                    <span class="text-caption text-grey">{{ task.location }}</span>
                  </div>
                  
                  <div class="d-flex align-center mb-1">
                    <v-icon size="small" class="mr-1" color="grey">mdi-clock-check</v-icon>
                    <span class="text-caption text-grey">Completed: {{ formatCompletedTime(task.completed_at) }}</span>
                  </div>

                  <div class="d-flex align-center">
                    <v-icon size="small" class="mr-1" color="success">mdi-timer</v-icon>
                    <span class="text-caption font-weight-bold text-success">
                      Total Time: {{ formatSeconds(task.total_time_seconds) }}
                    </span>
                  </div>
                </v-card-text>
              </v-card>
            </v-list>
          </v-window-item>

          <!-- Create Task Tab -->
          <v-window-item value="create">
            <v-card elevation="2">
              <v-card-title>Create New Task</v-card-title>
              <v-card-text>
                <v-form @submit.prevent="createTask">
                  <v-text-field
                    v-model="newTaskForm.title"
                    label="Task Title"
                    required
                    class="mb-4"
                  ></v-text-field>

                  <v-textarea
                    v-model="newTaskForm.description"
                    label="Description"
                    rows="3"
                    class="mb-4"
                  ></v-textarea>

                  <v-text-field
                    v-model="newTaskForm.location"
                    label="Location"
                    class="mb-4"
                  ></v-text-field>

                  <v-select
                    v-model="newTaskForm.priority"
                    :items="['Low', 'Medium', 'High']"
                    label="Priority"
                    class="mb-4"
                  ></v-select>

                  <v-alert
                    v-if="createMessage"
                    :type="createMessageType"
                    class="mb-4"
                    dismissible
                    @click:close="createMessage = ''"
                  >
                    {{ createMessage }}
                  </v-alert>

                  <v-btn
                    color="primary"
                    type="submit"
                    :loading="creatingTask"
                    block
                    size="large"
                  >
                    <v-icon start>mdi-plus</v-icon>
                    Create Task
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- Supply Requests Tab -->
          <v-window-item value="supplies">
            <v-card elevation="2" class="mb-4">
              <v-card-title class="d-flex justify-space-between align-center">
                <span>My Supply Requests</span>
                <v-btn color="primary" @click="openSupplyRequestDialog()">
                  <v-icon start>mdi-plus</v-icon>
                  New Request
                </v-btn>
              </v-card-title>
              <v-card-text v-if="loadingSupplyRequests" class="text-center py-4">
                <v-progress-circular indeterminate></v-progress-circular>
              </v-card-text>
              <v-card-text v-else-if="supplyRequests.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-package-variant-closed</v-icon>
                <p class="text-h6 text-grey">No supply requests</p>
                <p class="text-body-2 text-grey">Click "New Request" to submit a supply request.</p>
              </v-card-text>
              <v-card-text v-else>
                <div>
                  <v-card
                    v-for="request in supplyRequests"
                    :key="request.id"
                    class="mb-4"
                    elevation="3"
                    border
                  >
                    <v-card-title 
                      class="py-3"
                      :class="{
                        'bg-blue-grey-lighten-5': request.status === 'pending',
                        'bg-green-lighten-5': request.status === 'approved',
                        'bg-red-lighten-5': request.status === 'denied'
                      }"
                    >
                      <div class="d-flex align-center justify-space-between w-100">
                        <div class="d-flex align-center">
                          <v-avatar 
                            :color="request.status === 'pending' ? 'info' : request.status === 'approved' ? 'success' : 'error'" 
                            size="40" 
                            class="mr-3"
                          >
                            <v-icon color="white">
                              {{ request.status === 'pending' ? 'mdi-clock-outline' : request.status === 'approved' ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                          </v-avatar>
                          <div>
                            <div class="text-h6 font-weight-bold">{{ request.supply_name }}</div>
                            <div v-if="request.supply_number" class="text-caption text-blue-grey-darken-1">Item #: {{ request.supply_number }}</div>
                            <div class="text-caption" :class="{
                              'text-grey-darken-1': request.status === 'pending',
                              'text-success': request.status === 'approved',
                              'text-error': request.status === 'denied'
                            }">
                              {{ request.status === 'pending' ? 'Pending Approval' : request.status === 'approved' ? 'Approved' : 'Denied' }}
                            </div>
                          </div>
                        </div>
                        <v-chip :color="getSupplyStatusColor(request.status)" size="large" variant="flat">
                          <v-icon start>mdi-counter</v-icon>
                          {{ request.quantity }}
                        </v-chip>
                      </div>
                    </v-card-title>
                    
                    <v-divider></v-divider>
                    
                    <v-card-text class="py-4">
                      <v-row dense class="mb-2">
                        <v-col cols="12">
                          <div class="d-flex align-center pa-3 bg-grey-lighten-5 rounded">
                            <v-icon color="primary" class="mr-3">mdi-calendar-clock</v-icon>
                            <div>
                              <div class="text-caption text-grey-darken-2">Requested</div>
                              <div class="text-body-1 font-weight-bold">{{ formatCompletedTime(request.created_at) }}</div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      
                      <v-row v-if="request.reviewed_at" dense class="mb-2">
                        <v-col cols="12">
                          <div class="d-flex align-center pa-3 rounded" :class="request.status === 'approved' ? 'bg-green-lighten-5' : 'bg-red-lighten-5'">
                            <v-icon :color="request.status === 'approved' ? 'success' : 'error'" class="mr-3">
                              {{ request.status === 'approved' ? 'mdi-account-check' : 'mdi-account-cancel' }}
                            </v-icon>
                            <div>
                              <div class="text-caption text-grey-darken-2">{{ request.status === 'approved' ? 'Approved By' : 'Denied By' }}</div>
                              <div class="text-body-1 font-weight-bold">{{ request.reviewed_by_username }}</div>
                              <div class="text-caption">{{ formatCompletedTime(request.reviewed_at) }}</div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                      
                      <v-row v-if="request.deny_reason" dense class="mb-2">
                        <v-col cols="12">
                          <v-alert color="error" variant="tonal" density="compact" border="start" border-color="error">
                            <div class="text-subtitle-2 font-weight-bold mb-1">Denial Reason</div>
                            <div class="text-body-1">{{ request.deny_reason }}</div>
                          </v-alert>
                        </v-col>
                      </v-row>
                      
                      <v-row v-if="request.notes" dense>
                        <v-col cols="12">
                          <v-alert color="blue-grey-lighten-5" variant="tonal" density="compact">
                            <div class="text-caption font-weight-bold text-blue-grey-darken-3 mb-1">Notes</div>
                            <div class="text-body-2">{{ request.notes }}</div>
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    
                    <v-divider v-if="request.status === 'pending'"></v-divider>

                    <v-card-actions v-if="request.status === 'pending'" class="pa-4 pt-0">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <v-btn
                            size="large"
                            color="primary"
                            @click="openSupplyRequestDialog(request)"
                            block
                          >
                            <v-icon start>mdi-pencil</v-icon>
                            Edit
                          </v-btn>
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-btn
                            size="large"
                            color="error"
                            variant="outlined"
                            @click="deleteSupplyRequest(request)"
                            block
                          >
                            <v-icon start>mdi-delete</v-icon>
                            Delete
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card-actions>
                  </v-card>
                </div>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- My Inventory Tab -->
          <v-window-item value="my-inventory">
            <v-card elevation="2">
              <v-card-title>My Inventory</v-card-title>
              <v-card-text>
                <v-progress-linear v-if="loadingMyInventory" indeterminate></v-progress-linear>

                <div v-if="!loadingMyInventory && myInventoryItems.length === 0" class="text-center py-8">
                  <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-archive-off-outline</v-icon>
                  <p class="text-h6 text-grey">No items in your inventory</p>
                  <p class="text-body-2 text-grey">Items distributed to you will appear here.</p>
                </div>

                <v-list v-else class="pa-0">
                  <v-card
                    v-for="item in myInventoryItems"
                    :key="item.item_id"
                    class="mb-3"
                    elevation="1"
                  >
                    <v-card-text class="pb-2">
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div class="text-subtitle-1 font-weight-medium">{{ item.item_name }}</div>
                          <div v-if="item.item_number" class="text-caption text-grey">Item #: {{ item.item_number }}</div>
                        </div>
                        <v-chip color="teal" variant="flat">
                          {{ item.quantity }}
                        </v-chip>
                      </div>
                    </v-card-text>
                    <v-card-actions class="pt-0">
                      <v-btn color="error" variant="tonal" size="large" block @click="openMyRemoveDialog(item)">
                        <v-icon start>mdi-minus-circle</v-icon>
                        Remove Stock
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-list>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- Report Incident Tab -->
          <v-window-item value="incident">
            <v-card elevation="2">
              <v-card-title>Report an Incident</v-card-title>
              <v-card-text>
                <v-form ref="incidentForm" @submit.prevent="submitIncidentReport">
                  <v-text-field
                    v-model="incidentForm.title"
                    label="Incident Title *"
                    :rules="[v => !!v || 'Title is required']"
                    variant="outlined"
                    density="compact"
                    class="mb-3"
                  ></v-text-field>

                  <v-text-field
                    v-model="incidentForm.location"
                    label="Location *"
                    :rules="[v => !!v || 'Location is required']"
                    variant="outlined"
                    density="compact"
                    class="mb-3"
                  ></v-text-field>

                  <v-select
                    v-model="incidentForm.severity"
                    :items="incidentSeverityOptions"
                    label="Severity"
                    variant="outlined"
                    density="compact"
                    class="mb-3"
                  ></v-select>

                  <v-textarea
                    v-model="incidentForm.description"
                    label="Description"
                    variant="outlined"
                    density="compact"
                    rows="4"
                    auto-grow
                    class="mb-3"
                  ></v-textarea>

                  <v-alert
                    v-if="incidentSubmitMessage"
                    :type="incidentSubmitType"
                    class="mb-3"
                    density="compact"
                    @click:close="incidentSubmitMessage = ''"
                    closable
                  >{{ incidentSubmitMessage }}</v-alert>

                  <v-btn
                    color="orange-darken-2"
                    type="submit"
                    :loading="submittingIncident"
                    block
                    size="large"
                  >
                    <v-icon start>mdi-alert-circle</v-icon>
                    Submit Report
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-window-item>

          <!-- My Inspections Tab -->
          <v-window-item value="inspections">
            <v-card elevation="2">
              <v-card-title>My Inspections</v-card-title>
              <v-card-text>
                <v-progress-linear v-if="loadingInspections" indeterminate></v-progress-linear>

                <div v-if="!loadingInspections && myInspections.length === 0" class="text-center py-8">
                  <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-clipboard-text-off-outline</v-icon>
                  <p class="text-h6 text-grey">No inspections available</p>
                  <p class="text-body-2 text-grey">Completed inspections will appear here.</p>
                </div>

                <v-card
                  v-for="insp in myInspections"
                  :key="insp.id"
                  class="mb-3"
                  elevation="1"
                  @click="viewMyInspection(insp)"
                  style="cursor: pointer;"
                >
                  <v-card-text>
                    <div class="d-flex align-center justify-space-between mb-1">
                      <span class="text-subtitle-1 font-weight-medium">{{ formatInspectionDate(insp.completed_at || insp.created_at) }}</span>
                      <v-chip color="success" size="small">Complete</v-chip>
                    </div>
                    <div class="text-caption"><strong>Location:</strong> {{ insp.location }}</div>
                    <div class="text-caption"><strong>Inspector:</strong> {{ insp.inspector_username }}</div>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>

            <!-- Inspection Detail Dialog -->
            <v-dialog v-model="inspectionDetailDialog" max-width="800px" scrollable>
              <v-card v-if="selectedInspection">
                <v-card-title class="bg-deep-purple text-white d-flex align-center justify-space-between">
                  <span>Inspection — {{ formatInspectionDate(selectedInspection.completed_at || selectedInspection.created_at) }}</span>
                  <v-btn icon variant="text" color="white" @click="inspectionDetailDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="pt-4">
                  <v-row dense class="mb-3">
                    <v-col cols="6"><div class="text-caption text-grey">Location</div><div>{{ selectedInspection.location }}</div></v-col>
                    <v-col cols="6"><div class="text-caption text-grey">Inspector</div><div>{{ selectedInspection.inspector_username }}</div></v-col>
                    <v-col v-if="selectedInspection.administrator_name" cols="6"><div class="text-caption text-grey">Administrator</div><div>{{ selectedInspection.administrator_name }}</div></v-col>
                    <v-col v-if="selectedInspection.building_head" cols="6"><div class="text-caption text-grey">Building Head</div><div>{{ selectedInspection.building_head }}</div></v-col>
                  </v-row>
                  <div v-if="selectedInspection.notes" class="mb-3"><div class="text-caption text-grey">Notes</div><div class="text-body-2">{{ selectedInspection.notes }}</div></div>

                  <v-divider class="mb-3"></v-divider>

                  <div v-for="area in (selectedInspection.areas || [])" :key="area.id" class="mb-4">
                    <div class="text-subtitle-2 font-weight-bold mb-1">
                      {{ getAreaLabel(area.area_key) }}
                      <span v-if="area.descriptor" class="font-weight-regular text-grey"> — {{ area.descriptor }}</span>
                    </div>
                    <v-table density="compact" class="d-none d-md-block">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th class="text-center" style="width:140px">Rating</th>
                          <th v-if="areaHasNotes(area)">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="rating in (area.ratings || [])" :key="rating.id">
                          <td class="text-body-2">{{ getItemLabel(area.area_key, rating.item_key) }}</td>
                          <td class="text-center">
                            <v-chip :color="getInspRatingColor(rating.rating)" size="small" variant="flat">
                              {{ getInspRatingLabel(rating.rating) }}
                            </v-chip>
                          </td>
                          <td v-if="areaHasNotes(area)" class="text-body-2 text-grey-darken-1">{{ rating.notes || '' }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                    <div class="d-md-none">
                      <v-card
                        v-for="rating in (area.ratings || [])"
                        :key="'m-' + rating.id"
                        class="mb-1"
                        variant="outlined"
                        density="compact"
                      >
                        <v-card-text class="pa-2 d-flex align-center justify-space-between">
                          <span class="text-body-2" style="flex: 1; min-width: 0;">{{ getItemLabel(area.area_key, rating.item_key) }}</span>
                          <v-chip :color="getInspRatingColor(rating.rating)" size="small" variant="flat" class="ml-2 flex-shrink-0">
                            {{ getInspRatingLabel(rating.rating) }}
                          </v-chip>
                        </v-card-text>
                        <div v-if="rating.notes" class="px-2 pb-2 text-caption text-grey-darken-1">{{ rating.notes }}</div>
                      </v-card>
                    </div>
                    <div v-if="area.notes" class="text-caption text-grey-darken-1 mt-1 ml-2"><strong>Area Notes:</strong> {{ area.notes }}</div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="inspectionDetailDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-window-item>
        </v-window>
      </v-container>
    </v-main>

    <!-- Remove My Stock Dialog -->
    <v-dialog v-model="myRemoveDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">
          Remove Stock
        </v-card-title>
        <v-card-text>
          <div class="text-body-1 mb-3">
            <strong>{{ myRemoveItem?.item_name }}</strong>
            <span class="text-caption text-grey ml-2">(Available: {{ myRemoveItem?.quantity }})</span>
          </div>
          <v-text-field
            v-model.number="myRemoveQty"
            label="Quantity to Remove"
            type="number"
            min="1"
            :max="myRemoveItem?.quantity"
            variant="outlined"
            density="compact"
            class="mb-3"
            :rules="[v => v >= 1 || 'Must be at least 1', v => v <= (myRemoveItem?.quantity || 1) || 'Exceeds available quantity']"
          ></v-text-field>
          <v-text-field
            v-model="myRemoveReason"
            label="Reason (optional)"
            variant="outlined"
            density="compact"
            placeholder="e.g. Used for cleaning"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="myRemoveDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            :loading="removingMyStock"
            :disabled="!myRemoveQty || myRemoveQty < 1 || myRemoveQty > (myRemoveItem?.quantity || 1)"
            @click="removeMyStock"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark Incomplete Dialog -->
    <v-dialog v-model="incompleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6 bg-error text-white">
          <v-icon start color="white">mdi-alert-circle</v-icon>
          Mark Task as Incomplete
        </v-card-title>
        
        <v-card-text class="pt-4">
          <p class="mb-4 text-body-1">
            <strong>Task:</strong> {{ selectedTask?.title }}
          </p>
          
          <v-alert type="warning" density="compact" class="mb-4">
            This will mark the task as incomplete and it will appear in the admin's incomplete tasks list.
          </v-alert>
          
          <v-textarea
            v-model="incompleteReason"
            label="Reason for marking incomplete *"
            placeholder="Please explain why you cannot complete this task..."
            rows="4"
            variant="outlined"
            :rules="[v => !!v || 'Reason is required']"
            required
            counter
            hint="This information helps management understand any issues or obstacles."
          >
            <template v-slot:prepend-inner>
              <v-icon>mdi-comment-text</v-icon>
            </template>
          </v-textarea>
        </v-card-text>
        
        <v-card-actions>
          <v-btn
            color="grey"
            variant="text"
            @click="closeIncompleteDialog"
          >
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="elevated"
            @click="markTaskIncomplete"
            :loading="markingIncomplete"
            :disabled="!incompleteReason || incompleteReason.trim().length === 0"
          >
            <v-icon start>mdi-alert-circle</v-icon>
            Mark Incomplete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supply Request Dialog -->
    <v-dialog v-model="supplyRequestDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title>{{ editingSupplyRequest ? 'Edit Supply Request' : 'New Supply Request' }}</v-card-title>
        <v-card-text>

          <!-- EDIT MODE: single item form -->
          <template v-if="editingSupplyRequest">
            <v-autocomplete
              :model-value="supplyRequestSelectedItem"
              :items="inventoryItems"
              item-title="item_name"
              return-object
              label="Select Item *"
              variant="outlined"
              required
              clearable
              class="mb-2"
              @update:model-value="onSupplyItemSelected"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props" :subtitle="item.raw.item_number ? 'Item #: ' + item.raw.item_number : ''"></v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                {{ item.raw.item_name }}{{ item.raw.item_number ? ' — #' + item.raw.item_number : '' }}
              </template>
            </v-autocomplete>
            <v-text-field
              v-model.number="supplyRequestForm.quantity"
              label="Quantity *"
              type="number"
              min="1"
              variant="outlined"
              required
              class="mb-2"
            ></v-text-field>
            <v-textarea
              v-model="supplyRequestForm.notes"
              label="Notes"
              variant="outlined"
              rows="2"
              hide-details
            ></v-textarea>
          </template>

          <!-- NEW MODE: cart -->
          <template v-else>
            <!-- Add item row -->
            <v-row dense align="center" class="mb-2">
              <v-col cols="12" sm="7">
                <v-autocomplete
                  :model-value="supplyCartSelectedItem"
                  :items="inventoryItems"
                  item-title="item_name"
                  return-object
                  label="Select Item"
                  variant="outlined"
                  density="compact"
                  clearable
                  hide-details
                  @update:model-value="onCartItemSelected"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props" :subtitle="item.raw.item_number ? 'Item #: ' + item.raw.item_number : ''"></v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    {{ item.raw.item_name }}{{ item.raw.item_number ? ' — #' + item.raw.item_number : '' }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  v-model.number="supplyCartQty"
                  label="Qty"
                  type="number"
                  min="1"
                  variant="outlined"
                  density="compact"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="2">
                <v-btn
                  color="primary"
                  variant="flat"
                  block
                  :disabled="!supplyCartSelectedItem"
                  @click="addToCart"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <!-- Cart items list -->
            <div v-if="supplyCart.length > 0" class="mb-3">
              <v-divider class="mb-2"></v-divider>
              <div class="text-caption text-grey mb-2">Items to request ({{ supplyCart.length }})</div>
              <v-list density="compact" class="pa-0">
                <v-list-item
                  v-for="(cartItem, idx) in supplyCart"
                  :key="idx"
                  class="px-0"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary" size="small" class="mr-2">mdi-package-variant</v-icon>
                  </template>
                  <v-list-item-title class="text-body-2">{{ cartItem.supply_name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="cartItem.supply_number" class="text-caption">Item #: {{ cartItem.supply_number }}</v-list-item-subtitle>
                  <template v-slot:append>
                    <span class="text-body-2 font-weight-bold mr-3">×{{ cartItem.quantity }}</span>
                    <v-btn icon size="x-small" variant="text" color="error" @click="removeFromCart(idx)">
                      <v-icon size="small">mdi-close</v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </v-list>
              <v-divider class="mt-2 mb-3"></v-divider>
            </div>

            <v-alert v-else type="info" variant="tonal" density="compact" class="mb-3">
              Add at least one item above.
            </v-alert>

            <!-- Shared notes -->
            <v-textarea
              v-model="supplyRequestForm.notes"
              label="Notes (optional, applies to all items)"
              variant="outlined"
              rows="2"
              hide-details
            ></v-textarea>
          </template>

        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeSupplyRequestDialog">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="saveSupplyRequest"
            :loading="savingSupplyRequest"
            :disabled="editingSupplyRequest ? !supplyRequestForm.supply_name : supplyCart.length === 0"
          >
            {{ editingSupplyRequest ? 'Update' : `Submit${supplyCart.length > 1 ? ' (' + supplyCart.length + ' items)' : ''}` }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Alert Dialog -->
    <v-dialog v-model="alertDialog.show" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ alertDialog.title }}</v-card-title>
        <v-card-text>{{ alertDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="alertDialog.show = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmDialog.show" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="confirmDialog.show = false">Cancel</v-btn>
          <v-btn color="error" variant="text" @click="confirmDialog.action">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      tab: 'active',
      username: '',
      closetLocation: '',
      userLocation: '',
      isAdmin: false,
      currentDate: '',
      activeTasks: [],
      completedTasks: [],
      timers: {},
      loading: false,
      taskNotes: {},
      notesSaveTimers: {},
      searchQuery: '',
      newTaskForm: {
        title: '',
        description: '',
        location: '',
        priority: 'Medium'
      },
      creatingTask: false,
      createMessage: '',
      createMessageType: 'success',
      incompleteDialog: false,
      selectedTask: null,
      incompleteReason: '',
      markingIncomplete: false,
      supplyRequests: [],
      loadingSupplyRequests: false,
      supplyRequestDialog: false,
      editingSupplyRequest: null,
      inventoryItems: [],
      supplyRequestSelectedItem: null,
      supplyRequestForm: {
        supply_name: '',
        supply_number: '',
        quantity: 1,
        notes: ''
      },
      supplyCart: [],
      supplyCartSelectedItem: null,
      supplyCartQty: 1,
      savingSupplyRequest: false,

      // Inspections
      myInspections: [],
      myInspectionsCount: 0,
      loadingInspections: false,
      inspectionDetailDialog: false,
      selectedInspection: null,

      // My Inventory
      myInventoryItems: [],
      loadingMyInventory: false,
      myRemoveDialog: false,
      myRemoveItem: null,
      myRemoveQty: 1,
      myRemoveReason: '',
      removingMyStock: false,

      // Report Incident
      incidentForm: { title: '', location: '', severity: 'medium', description: '' },
      submittingIncident: false,
      incidentSubmitMessage: '',
      incidentSubmitType: 'success',
      incidentSeverityOptions: [
        { title: 'Low', value: 'low' },
        { title: 'Medium', value: 'medium' },
        { title: 'High', value: 'high' },
        { title: 'Critical', value: 'critical' }
      ],
      
      // Dialog states
      alertDialog: {
        show: false,
        title: 'Alert',
        message: ''
      },
      confirmDialog: {
        show: false,
        title: 'Confirm',
        message: '',
        action: null
      }
    }
  },
  computed: {
    filteredActiveTasks() {
      if (!this.searchQuery) return this.activeTasks
      const query = this.searchQuery.toLowerCase()
      return this.activeTasks.filter(task => 
        task.title.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        task.location?.toLowerCase().includes(query)
      )
    },
    filteredCompletedTasks() {
      if (!this.searchQuery) return this.completedTasks
      const query = this.searchQuery.toLowerCase()
      return this.completedTasks.filter(task => 
        task.title.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query) ||
        task.location?.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    this.loadUserInfo()
    this.setCurrentDate()
    this.loadTasks()
    this.loadSupplyRequests()
    this.loadInventoryItems()
    this.loadMyInspectionsCount()
    
    // Update elapsed time every second for in-progress tasks
    this.timerInterval = setInterval(() => {
      this.updateElapsedTimes()
    }, 1000)
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },
  methods: {
    loadUserInfo() {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      this.isAdmin = user.is_admin || false
      this.username = user.username || 'User'
      this.closetLocation = user.closet_location || null
      this.userLocation = user.location || ''
      // Pre-fill location fields with user's location
      this.newTaskForm.location = this.userLocation
      this.incidentForm.location = this.userLocation
    },
    setCurrentDate() {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
      this.currentDate = new Date().toLocaleDateString('en-US', options)
    },
    async loadTasks() {
      this.loading = true
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/tasks', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        // Check for auth errors
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('authToken')
          localStorage.removeItem('user')
          this.$router.push('/login')
          return
        }
        
        const data = await response.json()
        
        if (data.success) {
          this.activeTasks = data.tasks.filter(t => t.status !== 'completed')
          this.completedTasks = data.tasks.filter(t => t.status === 'completed')
          
          // Initialize timers for in-progress tasks
          this.activeTasks.forEach(task => {
            if (task.status === 'in_progress') {
              this.$set(this.timers, task.id, {
                startTime: new Date(),
                baseSeconds: task.total_time_seconds || 0
              })
            }
            // Initialize notes
            if (!this.taskNotes[task.id]) {
              this.$set(this.taskNotes, task.id, task.notes || '')
            }
          })
        }
      } catch (error) {
        console.error('Error loading tasks:', error)
      } finally {
        this.loading = false
      }
    },
    getPriorityColor(priority) {
      const colors = {
        'High': 'error',
        'Medium': 'warning',
        'Low': 'info'
      }
      return colors[priority] || 'grey'
    },
    formatTime(time) {
      if (!time) return 'N/A'
      return time.substring(0, 5) // Format HH:MM from HH:MM:SS
    },
    formatCompletedTime(timestamp) {
      if (!timestamp) return 'N/A'
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    },
    formatSeconds(totalSeconds) {
      if (!totalSeconds || totalSeconds === 0) return '0s'
      
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = Math.floor(totalSeconds % 60)
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`
      } else {
        return `${seconds}s`
      }
    },
    formatElapsedTime(task) {
      if (task.status === 'in_progress' && this.timers[task.id]) {
        const now = new Date()
        const elapsed = Math.floor((now - this.timers[task.id].startTime) / 1000)
        const total = this.timers[task.id].baseSeconds + elapsed
        return this.formatSeconds(total)
      }
      return this.formatSeconds(task.total_time_seconds || 0)
    },
    updateElapsedTimes() {
      // Force reactivity update for timer displays
      this.$forceUpdate()
    },
    async startTask(taskId) {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/tasks/${taskId}/start`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          await this.loadTasks()
        }
      } catch (error) {
        console.error('Error starting task:', error)
      }
    },
    async resumeTask(taskId) {
      await this.startTask(taskId)
    },
    async pauseTask(taskId) {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/tasks/${taskId}/pause`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Clean up timer
          if (this.timers[taskId]) {
            delete this.timers[taskId]
          }
          await this.loadTasks()
        }
      } catch (error) {
        console.error('Error pausing task:', error)
      }
    },
    async completeTask(taskId) {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/tasks/${taskId}/complete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        const data = await response.json()
        
        if (data.success) {
          // Clean up timer
          if (this.timers[taskId]) {
            delete this.timers[taskId]
          }
          await this.loadTasks()
        }
      } catch (error) {
        console.error('Error completing task:', error)
      }
    },
    debounceSaveNotes(taskId) {
      // Clear existing timer for this task
      if (this.notesSaveTimers[taskId]) {
        clearTimeout(this.notesSaveTimers[taskId])
      }
      
      // Set new timer to save after 1 second of inactivity
      this.notesSaveTimers[taskId] = setTimeout(() => {
        this.saveTaskNotes(taskId)
      }, 1000)
    },
    async saveTaskNotes(taskId) {
      try {
        const token = localStorage.getItem('authToken')
        await fetch(`https://app.timecustodian.work:4667/api/tasks/${taskId}/notes`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            notes: this.taskNotes[taskId] || ''
          })
        })
      } catch (error) {
        console.error('Error saving task notes:', error)
      }
    },
    async createTask() {
      if (!this.newTaskForm.title) {
        this.createMessage = 'Title is required'
        this.createMessageType = 'error'
        return
      }

      this.creatingTask = true
      this.createMessage = ''

      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/tasks', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.newTaskForm)
        })

        const data = await response.json()

        if (data.success) {
          this.createMessage = 'Task created successfully!'
          this.createMessageType = 'success'
          this.newTaskForm = {
            title: '',
            description: '',
            location: this.userLocation,
            priority: 'Medium'
          }
          await this.loadTasks()
          // Switch to active tab to see the new task
          this.tab = 'active'
        } else {
          this.createMessage = data.message || 'Failed to create task'
          this.createMessageType = 'error'
        }
      } catch (error) {
        console.error('Error creating task:', error)
        this.createMessage = 'Error creating task'
        this.createMessageType = 'error'
      } finally {
        this.creatingTask = false
      }
    },
    logout() {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      this.$router.push('/login')
    },
    async loadInventoryItems() {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/inventory/items-list', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.inventoryItems = data.items
        }
      } catch (error) {
        console.error('Error loading inventory items:', error)
      }
    },
    async loadSupplyRequests() {
      this.loadingSupplyRequests = true
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/supply-requests', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()

        if (data.success) {
          this.supplyRequests = data.requests
        }
      } catch (error) {
        console.error('Error loading supply requests:', error)
      } finally {
        this.loadingSupplyRequests = false
      }
    },
    openSupplyRequestDialog(request = null) {
      if (request) {
        this.editingSupplyRequest = request
        this.supplyRequestSelectedItem = this.inventoryItems.find(i => i.item_name === request.supply_name) || null
        this.supplyRequestForm = {
          supply_name: request.supply_name,
          supply_number: request.supply_number || '',
          quantity: request.quantity,
          notes: request.notes || ''
        }
      } else {
        this.editingSupplyRequest = null
        this.supplyRequestSelectedItem = null
        this.supplyRequestForm = { supply_name: '', supply_number: '', quantity: 1, notes: '' }
        this.supplyCart = []
        this.supplyCartSelectedItem = null
        this.supplyCartQty = 1
      }
      this.supplyRequestDialog = true
    },
    closeSupplyRequestDialog() {
      this.supplyRequestDialog = false
      this.editingSupplyRequest = null
      this.supplyRequestSelectedItem = null
      this.supplyRequestForm = { supply_name: '', supply_number: '', quantity: 1, notes: '' }
      this.supplyCart = []
      this.supplyCartSelectedItem = null
      this.supplyCartQty = 1
    },
    onSupplyItemSelected(item) {
      this.supplyRequestSelectedItem = item || null
      if (item) {
        this.supplyRequestForm.supply_name = item.item_name
        this.supplyRequestForm.supply_number = item.item_number || ''
      } else {
        this.supplyRequestForm.supply_name = ''
        this.supplyRequestForm.supply_number = ''
      }
    },
    onCartItemSelected(item) {
      this.supplyCartSelectedItem = item || null
    },
    addToCart() {
      if (!this.supplyCartSelectedItem) return
      this.supplyCart.push({
        supply_name: this.supplyCartSelectedItem.item_name,
        supply_number: this.supplyCartSelectedItem.item_number || '',
        quantity: parseInt(this.supplyCartQty) || 1
      })
      this.supplyCartSelectedItem = null
      this.supplyCartQty = 1
    },
    removeFromCart(index) {
      this.supplyCart.splice(index, 1)
    },
    async saveSupplyRequest() {
      this.savingSupplyRequest = true
      try {
        const token = localStorage.getItem('authToken')

        if (this.editingSupplyRequest) {
          // Single-item edit
          if (!this.supplyRequestForm.supply_name || parseInt(this.supplyRequestForm.quantity) < 1) return
          this.supplyRequestForm.quantity = parseInt(this.supplyRequestForm.quantity) || 1
          const response = await fetch(`https://app.timecustodian.work:4667/api/supply-requests/${this.editingSupplyRequest.id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(this.supplyRequestForm)
          })
          const data = await response.json()
          if (!data.success) {
            this.showAlert('Error', data.message || 'Failed to update supply request')
            return
          }
        } else {
          // Multi-item cart: one POST per item
          if (this.supplyCart.length === 0) return
          for (const cartItem of this.supplyCart) {
            const response = await fetch('https://app.timecustodian.work:4667/api/supply-requests', {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                supply_name: cartItem.supply_name,
                supply_number: cartItem.supply_number,
                quantity: cartItem.quantity,
                notes: this.supplyRequestForm.notes
              })
            })
            const data = await response.json()
            if (!data.success) {
              this.showAlert('Error', data.message || `Failed to submit request for "${cartItem.supply_name}"`)
              return
            }
          }
        }

        this.closeSupplyRequestDialog()
        await this.loadSupplyRequests()
      } catch (error) {
        console.error('Error saving supply request:', error)
        this.showAlert('Error', 'Error saving supply request')
      } finally {
        this.savingSupplyRequest = false
      }
    },
    async deleteSupplyRequest(request) {
      if (!confirm(`Delete supply request for "${request.supply_name}"?`)) {
        return
      }

      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/supply-requests/${request.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()

        if (data.success) {
          await this.loadSupplyRequests()
        } else {
          this.showAlert('Error', data.message || 'Failed to delete supply request')
        }
      } catch (error) {
        console.error('Error deleting supply request:', error)
        this.showAlert('Error', 'Error deleting supply request')
      }
    },
    getSupplyStatusColor(status) {
      switch (status) {
        case 'approved': return 'success'
        case 'denied': return 'error'
        default: return 'warning'
      }
    },
    openIncompleteDialog(task) {
      this.selectedTask = task
      this.incompleteReason = ''
      this.incompleteDialog = true
    },
    closeIncompleteDialog() {
      this.incompleteDialog = false
      this.selectedTask = null
      this.incompleteReason = ''
      this.markingIncomplete = false
    },
    async markTaskIncomplete() {
      if (!this.incompleteReason || this.incompleteReason.trim().length === 0) {
        return
      }

      this.markingIncomplete = true

      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/tasks/${this.selectedTask.id}/mark-incomplete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: this.incompleteReason.trim()
          })
        })

        const data = await response.json()

        if (data.success) {
          // Clean up timer if exists
          if (this.timers[this.selectedTask.id]) {
            delete this.timers[this.selectedTask.id]
          }
          
          this.closeIncompleteDialog()
          await this.loadTasks()
        } else {
          this.showAlert('Error', data.message || 'Failed to mark task as incomplete')
        }
      } catch (error) {
        console.error('Error marking task as incomplete:', error)
        this.showAlert('Error', 'Error marking task as incomplete')
      } finally {
        this.markingIncomplete = false
      }
    },
    
    // Dialog helper methods
    showAlert(title, message) {
      this.alertDialog = {
        show: true,
        title,
        message
      }
    },
    async loadMyInventory() {
      this.loadingMyInventory = true
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/my-inventory', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.myInventoryItems = data.items
        }
      } catch (error) {
        console.error('Error loading inventory:', error)
      } finally {
        this.loadingMyInventory = false
      }
    },
    openMyRemoveDialog(item) {
      this.myRemoveItem = item
      this.myRemoveQty = 1
      this.myRemoveReason = ''
      this.myRemoveDialog = true
    },
    async removeMyStock() {
      if (!this.myRemoveItem || this.myRemoveQty < 1) return
      this.removingMyStock = true
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/my-inventory/${this.myRemoveItem.item_id}/remove`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: parseInt(this.myRemoveQty), reason: this.myRemoveReason || null })
        })
        const data = await response.json()
        if (data.success) {
          this.myRemoveDialog = false
          await this.loadMyInventory()
        } else {
          this.showAlert('Error', data.message || 'Failed to remove stock')
        }
      } catch (error) {
        console.error('Error removing stock:', error)
        this.showAlert('Error', 'Error removing stock')
      } finally {
        this.removingMyStock = false
      }
    },
    async submitIncidentReport() {
      const { valid } = await this.$refs.incidentForm.validate()
      if (!valid) return
      this.submittingIncident = true
      this.incidentSubmitMessage = ''
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/report-incident', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(this.incidentForm)
        })
        const data = await response.json()
        if (data.success) {
          this.incidentSubmitMessage = 'Incident report submitted successfully.'
          this.incidentSubmitType = 'success'
          this.incidentForm = {
            title: '',
            location: this.userLocation,
            severity: 'medium',
            description: ''
          }
          this.$refs.incidentForm.resetValidation()
        } else {
          this.incidentSubmitMessage = data.message || 'Failed to submit report'
          this.incidentSubmitType = 'error'
        }
      } catch (error) {
        console.error('Error submitting incident:', error)
        this.incidentSubmitMessage = 'Error submitting report'
        this.incidentSubmitType = 'error'
      } finally {
        this.submittingIncident = false
      }
    },
    async loadMyInspectionsCount() {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/admin/inspections/my/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.myInspectionsCount = data.inspections.length
        }
      } catch (error) {
        // silent
      }
    },
    async loadMyInspections() {
      this.loadingInspections = true
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch('https://app.timecustodian.work:4667/api/admin/inspections/my/list', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.myInspections = data.inspections
          this.myInspectionsCount = data.inspections.length
        }
      } catch (error) {
        console.error('Error loading inspections:', error)
      } finally {
        this.loadingInspections = false
      }
    },
    async viewMyInspection(insp) {
      try {
        const token = localStorage.getItem('authToken')
        const response = await fetch(`https://app.timecustodian.work:4667/api/admin/inspections/my/${insp.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.selectedInspection = data.inspection
          this.inspectionDetailDialog = true
        }
      } catch (error) {
        console.error('Error loading inspection detail:', error)
      }
    },
    getAreaLabel(areaKey) {
      const labels = {
        building_entrance: 'Building Entrances',
        restrooms: 'Restrooms',
        cafeteria: 'Cafeteria/Lunchroom/Kitchens',
        classrooms: 'Classrooms',
        hallways_stairs: 'Hallways and Stairs',
        custodial_closets: 'Custodial Closets',
        gymnasium_locker: 'Gymnasium and Locker Rooms',
        equipment: 'Equipment',
        boiler_mech_storage: 'Boiler Room, Mech Rooms, Storage Rooms/Offices'
      }
      return labels[areaKey] || areaKey
    },
    getItemLabel(areaKey, itemKey) {
      // Inline lookup to avoid importing the full areas constant
      return itemKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
    getInspRatingLabel(value) {
      if (value == null) return 'Not Evaluated'
      if (value === 1) return 'Exceeds'
      if (value === 2) return 'Meets'
      if (value === 3) return 'Below'
      return 'Unknown'
    },
    getInspRatingColor(value) {
      if (value == null) return 'grey'
      if (value === 1) return 'success'
      if (value === 2) return 'info'
      if (value === 3) return 'error'
      return 'grey'
    },
    areaHasNotes(area) {
      return (area.ratings || []).some(r => r.notes)
    },
    formatInspectionDate(dateStr) {
      if (!dateStr) return 'N/A'
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}

.text-h4 {
  line-height: 1.2;
}

.task-in-progress {
  border-left: 4px solid rgb(var(--v-theme-primary));
}
</style>
